import { z } from "zod";
import i18n from "@bcc/lib/i18n";
import { zoneFromPosition } from "@shared/lib/intensity/intensity-estimator";
import { isIntensityPosition } from "@shared/types/intensity";
import { createProductFieldsSchema, productFieldsSchema, type ProductFieldsSchemaConfig } from "./product-fields-schema";
import { createVariantSchema, variantSchema, type VariantSchemaConfig } from "./variant-schema";
import { REQUIRED_GRIND } from "../constants";

const V = "views.shopifyImportModal.productForm.validation" as const;

export type ProductSchemaConfig = VariantSchemaConfig & ProductFieldsSchemaConfig;

const buildProductSchema = (
  variantSchemaInstance: ReturnType<typeof createVariantSchema>,
  fieldsSchema: ReturnType<typeof createProductFieldsSchema> = productFieldsSchema
) =>
  fieldsSchema
    .extend({
      // Variants array
      variants: z.array(variantSchemaInstance),

      // Optional fields from FormProduct interface
      uuid: z.string().optional(),
      localVariants: z.array(variantSchemaInstance).optional(),
      localTitle: z.string().optional(),
      isProductFormValid: z.boolean(),
      hasDraft: z.boolean().optional(),
      hasVariantsDraft: z.boolean().optional(),
    })
    .superRefine((product, ctx) => {
      // Cross-field validation 1: elevation end >= start
      if (product.elevationEnd < product.elevationStart) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t(`${V}.elevationEndGteStart`),
          path: ["elevationEnd"],
        });
      }

      // Cross-field validation 2: at least one variant selected
      const selectedVariants = product.variants.filter((variant) => variant.isProductSelectedForImport);
      if (selectedVariants.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t(`${V}.atLeastOneVariant`),
          path: ["variants"],
        });
      }

      // Cross-field validation 3a: the score is both-or-neither. The zone is
      // always derived from the position, so a half-populated pair means
      // something wrote one without the other — and the payload builders
      // serialise the two independently, so it would reach Salesforce.
      // Defence in depth: the UI recomputes the zone on every change.
      const hasZone = Boolean(product.intensityZone);
      const hasPosition = isIntensityPosition(product.intensityPosition);
      if (hasZone !== hasPosition) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t(`${V}.intensityIncomplete`),
          path: [hasZone ? "intensityPosition" : "intensityZone"],
        });
      }

      // Cross-field validation 3b: the zone must match the position it was
      // derived from. The zone is never entered directly, so a mismatch means
      // something wrote one of the pair without recomputing the other.
      if (product.intensityZone && isIntensityPosition(product.intensityPosition)) {
        const expectedZone = zoneFromPosition(product.intensityPosition);
        if (product.intensityZone !== expectedZone) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            // Translate the zone before interpolating it — the raw key
            // (`brightAndLively`) is an implementation token, not display copy.
            message: i18n.t(`${V}.intensityZoneMismatch`, {
              zone: i18n.t(`views.shopifyImportModal.productForm.intensityZones.${expectedZone}`),
            }),
            path: ["intensityZone"],
          });
        }
      }

      // Cross-field validation 5: required grind per bag size
      // Build set of bag sizes already covered by PIM-imported local variants
      const localBagSizes = new Set(
        (product.localVariants ?? []).map((variant) => variant.mapDetectedBagSizeTo ?? variant.bagSize).filter(Boolean)
      );

      // Get unique bag sizes from selected variants (filter out null/empty)
      const bagSizes = [
        ...new Set(
          selectedVariants
            .map((variant) => variant.mapDetectedBagSizeTo ?? variant.bagSize)
            .filter((bagSize): bagSize is string => Boolean(bagSize))
        ),
      ];

      // For each bag size, ensure at least one variant has REQUIRED_GRIND
      for (const bagSize of bagSizes) {
        // Skip bag sizes already imported via PIM (they already satisfy grind requirements)
        if (localBagSizes.has(bagSize)) continue;

        const variantsForBagSize = selectedVariants.filter((variant) => (variant.mapDetectedBagSizeTo ?? variant.bagSize) === bagSize);
        const hasRequiredGrind = variantsForBagSize.some((variant) => variant.grind.includes(REQUIRED_GRIND));

        if (!hasRequiredGrind) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t(`${V}.requiredGrindPerBagSize`, {
              bagSize,
              grind: REQUIRED_GRIND,
            }),
            path: ["variants"],
          });
        }
      }
    });

export const createProductSchema = (config: ProductSchemaConfig = {}) => {
  const { imageOptional, readOnlyProductFields, titleMaxLength, badgesEnabled, ...variantConfig } = config;
  return buildProductSchema(
    createVariantSchema(variantConfig),
    createProductFieldsSchema({ imageOptional, readOnlyProductFields, titleMaxLength, badgesEnabled })
  );
};

// Default schema with standard inventory bounds — use createProductSchema for config-driven bounds
export const productSchema = buildProductSchema(variantSchema);

// Product-level fields for an imported product are prefilled from PIM and
// rendered disabled, so their *content* must not gate validity. `.partial()`
// alone only makes them optional — a present-but-over-long title would still
// fail, leaving a locked product permanently unimportable with no field the
// roaster is allowed to fix. `readOnlyProductFields` drops the min/max rules
// on those fields, which is what the contract below has always claimed.
const importedFieldsSchema = createProductFieldsSchema({ readOnlyProductFields: true });

// Schema for imported products: product-level fields are pre-populated from
// PIM and locked (disabled). They may contain invalid or missing data that the
// user cannot fix, so only variant-level fields and cross-field rules are
// validated. Product-level fields are made optional via passthrough.
const buildImportedProductSchema = (
  variantSchemaInstance: ReturnType<typeof createVariantSchema>,
  fieldsSchema: ReturnType<typeof createProductFieldsSchema> = importedFieldsSchema
) =>
  fieldsSchema
    .partial()
    .extend({
      // Keep variant validation strict
      variants: z.array(variantSchemaInstance),
      uuid: z.string().optional(),
      localVariants: z.array(variantSchemaInstance).optional(),
      localTitle: z.string().optional(),
      isProductFormValid: z.boolean(),
      hasDraft: z.boolean().optional(),
      hasVariantsDraft: z.boolean().optional(),
      // Accept any additional fields from PIM without failing
      imageUploads: z.unknown().optional(),
      elevationStart: z.number().optional(),
      elevationEnd: z.number().optional(),
    })
    .passthrough()
    .superRefine((product, ctx) => {
      // Only validate: at least one variant selected
      const selectedVariants = product.variants.filter((v) => v.isProductSelectedForImport);
      if (selectedVariants.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t(`${V}.atLeastOneVariant`),
          path: ["variants"],
        });
      }
    });

export const importedProductSchema = buildImportedProductSchema(variantSchema);

export const createImportedProductSchema = (config: ProductSchemaConfig = {}) => {
  const { imageOptional, titleMaxLength, badgesEnabled, ...variantConfig } = config;
  return buildImportedProductSchema(
    createVariantSchema(variantConfig),
    // `readOnlyProductFields` is forced on: every product routed here has its
    // product-level fields locked, whatever the caller passed.
    createProductFieldsSchema({ imageOptional, titleMaxLength, badgesEnabled, readOnlyProductFields: true })
  );
};

// Export inferred type for use in components
export type ProductFormData = z.infer<typeof productSchema>;
